import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import { Translate } from '../../utils/translations'
import InputField from './fields/input'
import SelectField from './fields/select'
import YesNoButtonsField from './fields/yes-no-buttons'
import { phonePrefixesAsSelectionOptions } from '../../constants/countries'
import { campaignApi } from '../../campaigns/api'
import type {
  EmailConfirmationDto,
  OwnerChangedDto,
  UnknownNewOwnerDto,
} from '../../api/generated/models'

/**
 * Validation function for owner changed form
 * Either email OR phone with prefix is required
 */
const validateForm = (values: any) => {
  const errors: any = {}

  // Only validate if user selected "yes"
  if (values.hasOwnerChanged === 'yes') {
    const hasEmail =
      values.newOwner.email &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.newOwner.email)
    const hasPhone = values.newOwner.phonePrefix && values.newOwner.phone

    // At least one contact method is required
    if (!hasEmail && !hasPhone) {
      errors.newOwner = {
        ...errors.newOwner,
        email: <Translate value="fields.validations.phoneOrEmail" />,
        phone: <Translate value="fields.validations.phoneOrEmail" />,
      }
    }

    // If email is provided, validate its format
    if (
      values.newOwner.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.newOwner.email)
    ) {
      errors.newOwner = {
        ...errors.newOwner,
        email: <Translate value="fields.validations.email" />,
      }
    }

    // If phone is provided, both prefix and number are required
    if (values.newOwner.phone && !values.newOwner.phonePrefix) {
      errors.newOwner = { ...errors.newOwner, phonePrefix: <Translate value="fields.required" /> }
    }

    if (values.newOwner.phonePrefix && !values.newOwner.phone) {
      errors.requestor = { ...errors.requestor, phone: <Translate value="fields.required" /> }
    }
  }

  return errors
}

/**
 * Build the initial values for the owner changed form, pre-filling them from
 * previously saved raw values when the user navigates back to this step.
 * @param savedValues - Raw form values saved on a previous visit
 */
const buildInitialValues = (savedValues?: Record<string, any>) => ({
  hasOwnerChanged: savedValues?.hasOwnerChanged ?? '',
  newOwner: {
    email: savedValues?.newOwner?.email ?? '',
    phonePrefix: savedValues?.newOwner?.phonePrefix ?? '',
    phone: savedValues?.newOwner?.phone ?? '',
  },
})

interface OwnerChangedFormProps {
  onSuccess?: (result: any) => void
  onError?: (error: any) => void
  onUnknownNewOwner?: () => void
  requestor: EmailConfirmationDto['requestor']
  language: string
  /** Wizard journey ID from email confirmation (required for API calls). */
  submissionId?: string
  /** Raw values entered on a previous visit, used to pre-fill the form. */
  savedValues?: Record<string, any>
  /** Persists the raw form values for back/forward navigation. */
  onSaveValues?: (values: Record<string, any>) => void
}

/**
 * Owner changed form component using Formik
 * Handles the creation of owner changed entries for basketball hoop recalls
 */
const OwnerChangedForm: React.FC<OwnerChangedFormProps> = ({
  onSuccess,
  onError,
  onUnknownNewOwner,
  requestor,
  language,
  submissionId,
  savedValues,
  onSaveValues,
}) => {
  // Hooks for the owner changed mutations (campaign-specific endpoints)
  const useCreateOwnerChanged = campaignApi.useCreateOwnerChanged
  const useCreateUnknownNewOwner = campaignApi.useCreateUnknownNewOwner

  const ownerChangedMutation = useCreateOwnerChanged({
    mutation: {
      onSuccess: data => {
        onSuccess?.(data)
      },
      onError: error => {
        onError?.(error)
      },
    },
  })

  const unknownNewOwnerMutation = useCreateUnknownNewOwner({
    mutation: {
      onSuccess: data => {
        onUnknownNewOwner?.()
      },
    },
  })

  /**
   * Handle form submission
   * @param values - Form values from Formik
   * @param actions - Formik actions for form management
   */
  const handleSubmit = async (values: any, actions: any) => {
    if (!submissionId) {
      actions.setSubmitting(false)
      return
    }
    try {
      // Persist a snapshot of the raw values (before null normalization) so the
      // step is pre-filled with the actual user input when navigating back.
      onSaveValues?.({
        hasOwnerChanged: values.hasOwnerChanged,
        newOwner: { ...values.newOwner },
      })

      if (values.newOwner.email === '') values.newOwner.email = null
      if (values.newOwner.phone === '') values.newOwner.phone = null
      if (values.newOwner.phonePrefix === '') values.newOwner.phonePrefix = null

      // Only submit if user selected "yes"
      if (values.hasOwnerChanged === 'yes') {
        const ownerChangedData: OwnerChangedDto = {
          submissionId,
          requestor: {
            country: requestor.country,
            email: requestor.email,
            language,
          },
          newOwner: values.newOwner,
        }
        await ownerChangedMutation.mutateAsync({ data: ownerChangedData })
      } else {
        const unknownNewOwnerData: UnknownNewOwnerDto = {
          submissionId,
          requestor: {
            country: requestor.country,
            email: requestor.email,
            language,
          },
        }
        await unknownNewOwnerMutation.mutateAsync({ data: unknownNewOwnerData })
      }
      actions.setSubmitting(false)
    } catch (error) {
      actions.setSubmitting(false)
      // Error is handled by the mutation onError callback
    }
  }

  return (
    <Element>
      <Formik
        enableReinitialize
        initialValues={buildInitialValues(savedValues)}
        validate={validateForm}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, values }) => (
          <Form>
            <Field
              name="hasOwnerChanged"
              component={YesNoButtonsField}
              label={<Translate value="ownerChanged.title" />}
            />

            {values.hasOwnerChanged === 'yes' && (
              <>
                <Field
                  name="newOwner.email"
                  component={InputField}
                  type="email"
                  label={<Translate value="fields.email" />}
                />

                <Columns>
                  <Columns.Column size={4}>
                    <Field
                      name="newOwner.phonePrefix"
                      component={SelectField}
                      label={<Translate value="fields.phonePrefix" />}
                      options={phonePrefixesAsSelectionOptions}
                      selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
                    />
                  </Columns.Column>
                  <Columns.Column size={8}>
                    <Field
                      name="newOwner.phone"
                      component={InputField}
                      type="tel"
                      label={<Translate value="fields.phone" />}
                    />
                  </Columns.Column>
                </Columns>
              </>
            )}

            <Button
              type="submit"
              color="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              fullwidth
              data-testid="step-submit"
            >
              <Translate value="form.submit" />
            </Button>
          </Form>
        )}
      </Formik>
    </Element>
  )
}

export default OwnerChangedForm
